<template>
    <div class="search position--relative">
        <form
            id="search"
            class="search__commands position--relative display--flex"
            @submit.prevent="search"
            novalidate
        >
            <div class="search__bar display--flex position--relative">
                <input
                    type="text"
                    class="search__bar__input"
                    :placeholder="$t('global.search')"
                    :aria-label="$t('global.search')"
                    v-model="name"
                >
                <span
                    v-if="showReset"
                    class="search__bar__reset position--absolute capitalize pointer"
                    role="button"
                    @click="resetSearch"
                >
                    {{ $t('global.clear') }}
                </span>
                <base-button
                    form="search"
                    type="submit"
                    class="search__bar__button display--flex color--green"
                    :hasHover="false"
                    :customClass="true"
                >
                    <template #button__icon>
                        <icon-lup />
                    </template>
                </base-button>
            </div>
            <div class="position--relative search__calendar__container" v-if="showSearchFilters">
                <icon-calendar class="search__calendar__icon position--absolute" />
                <base-field
                    class="search__calendar"
                    :settings="{
                        input: {
                            component: 'InputRangeCalendar',
                            name: 'searchRange',
                            id: 'searchRange',
                            targetClass: 'search__calendar__input field__input--default typography--default color--default',
                            value: date,
                            browserAutocomplete: 'off',
                            noListener: true,
                            searchField: true
                        },
                        allowFilling: true,
                        reset: resetOptions
                    }"
                />
            </div>
            <base-field
                v-if="showSearchFilters"
                class="search__states"
                :settings="{
                    input: {
                        component: 'InputAutocomplete',
                        name: 'contactCallState',
                        id: 'contactCallState',
                        type: 'text',
                        value: contactCallState,
                        targetClass: 'search__states__input field__input--default typography--default color--default',
                        browserAutocomplete: 'off',
                        placeholder: $t('global.state'),
                        dynamicData: {
                            data: states,
                        },
                        searchField: true,
                        noListener: true
                    },
                    allowFilling: true
                }"
            />
            <base-field
                v-if="showSearchFilters"
                class="search__campaigns"
                :settings="{
                    input: {
                        component: 'InputAutocomplete',
                        name: 'campaignName',
                        id: 'campaignName',
                        type: 'text',
                        value: campaignName,
                        targetClass: 'search__campaigns__input field__input--default typography--default color--default',
                        browserAutocomplete: 'off',
                        placeholder: $t('global.campaign'),
                        dynamicData: {
                            data: campaigns,
                        },
                        searchField: true,
                        noListener: true
                    },
                    allowFilling: true
                }"
            />
        </form>
    </div>
</template>
<script>
// packages
import {
    reactive,
    toRefs,
    watch,
    computed
} from '@vue/composition-api';

// utils
import { haveProperty } from '@/utils';

// configs
import { contactCallStates } from '@/configs/const';

// store
import { mapState, useStore } from '@/hooks/use-store';

// helpers
import { get } from '@/helpers/requests';
import { sortObjectsArray } from '@/helpers/mixinGeneral';

export default {
    components: {
        BaseButton: () => import( /* webpackChunkName: 'BaseButton' */ '@/components/bundles/buttons/BaseButton' ),
        IconLup: () => import( /* webpackChunkName: 'IconLup' */ '@/components/bundles/layout/images/icons/IconLup' ),
        BaseField: () => import( /* webpackChunkName: 'BaseField'  */ '@/components/bundles/fields/BaseField' ),
        IconCalendar: () => import( /* webpackChunkName: 'IconCalendar' */ '@/components/bundles/layout/images/icons/IconCalendar' )
    },
    setup ( _, { root }) {
        // Obter ações da store
        const { actions } = useStore();

        // configurações de pesquisa.
        const settings = reactive({
            name: '',
            date: '',
            contactCallState: '',
            campaignName: '',
            showSearchFilters: computed( () => [ 'audit' ].includes( root.$route.name ) ),
            isSinglePage: false,
            resetOptions: false,
            hasChangedRoute: false
        });

        // lista de estados
        const requestData = reactive({
            states: Object.keys( contactCallStates ).map( key => ({ value: key, label: contactCallStates[key] }) ),
            campaigns: []
        });

        // show search filters (data e state)
        watch( () => settings.showSearchFilters,
            newVal => {
                if ( newVal && !requestData?.campaigns?.length )
                    getCampaignsRequest();
            });

        const getCampaignsRequest = ( async () => {
            if ( settings.showSearchFilters ) {
                requestData.campaigns = await get( '/campaigns' )
                    .then( ({ data }) => data.content.data );
                requestData.campaigns = requestData.campaigns.map( key => key.name ).map( name => ({ value: name, label: name }) );
                // ordenar drop dos das campanhas
                sortObjectsArray( requestData.campaigns );
            }
        });

        getCampaignsRequest();

        // ordenar drop dos estados
        sortObjectsArray( requestData.states );

        const showReset = computed( () => settings.name.length > 0 );

        const searchData = mapState({ module: 'storePayload', key: 'searchData' });

        /**
         * Pesquisa
         * Adiciona ou altera a query da rota em
         * que o utilizador está, com o termo inserido na input
         * ou as opções selecionadas nas drops.
         *
         * * @since 1.2.5
         * @category Method
         * @autor Jorge Monteiro
         *
        */
        const search = () => {
            const { date, ...searchDataEntries } = searchData.value;

            const newRoute = {
                ...( settings.isSinglePage && { name: root.$route.meta.parent }),
                query: {
                    ...searchDataEntries,
                    ...( searchData.value.toReceptionDate
                        && {
                            toReceptionDate: searchData.value.toReceptionDate,
                            fromReceptionDate: searchData.value.fromReceptionDate
                        }
                    ),
                    ...( settings.name && { name: settings.name })
                }
            };

            root.$router.push({ ...newRoute }).catch( () => {});
        };

        /**
         * Reset pesquisa de texto
         * Limpa o valor da input de texto e a
         * query da route.
         *
         * * @since 1.2.5
         * @category Method
         * @autor Jorge Monteiro
         *
        */
        const resetSearch = () => {
            settings.name = '';
            // eslint-disable-next-line
            const { name, ...queryWithoutSearch } = root.$route.query;
            root.$router.push({ query: queryWithoutSearch }).catch( () => {});
        };

        // Sempre que o utilizador altere a rota
        // irá validar se já existe alguma query e
        // se a nova página é uma single page
        // ( campanha, utilizador, etc )
        watch( () => root.$route,
            ({ query, meta }) => {
                settings.name = query.name || '';
                settings.date = query.fromReceptionDate ? `${query.fromReceptionDate} - ${query.toReceptionDate}` : '';
                settings.isSinglePage = haveProperty( meta, 'model' );

                /* eslint-disable */
                const {
                    name,
                    campaignName,
                    contactCallState,
                    fromReceptionDate,
                    toReceptionDate,
                    ...arrayParams
                } = query;
                /* eslint-enable */

                const filtersNotArray = {
                    name,
                    campaignName,
                    contactCallState,
                    fromReceptionDate,
                    toReceptionDate
                };

                Object.keys( filtersNotArray ).forEach( key => {
                    actions( 'saveSearchData', { [key]: filtersNotArray[key] });
                    settings[key] = filtersNotArray[key] || '';
                });

                Object.keys( arrayParams ).forEach( key => {
                    const varIsArray = Array.isArray( arrayParams[key] );

                    actions( 'saveSearchData', { [key]: ( varIsArray ) ? arrayParams[key] : [ arrayParams[key] ] });
                    settings[key] = ( varIsArray ) ? arrayParams[key] : [ arrayParams[key] ];
                });
            }, {
                immediate: true
            });

        // Sempre que o utilizador altere a página
        // irá resetar toda a pesquisa
        watch( () => root.$route.name,
            newRouteName => {
                if ( newRouteName ) {
                    settings.hasChangedRoute = true;

                    setTimeout( () => {
                        settings.hasChangedRoute = false;
                    }, 300 );
                }
            });

        watch( () => searchData.value,
            ( newVal, oldVal ) => {
                const oldValues = { ...oldVal };
                delete oldValues.page;
                const newValues = { ...newVal };
                delete newValues.page;

                if ( !settings.hasChangedRoute && JSON.stringify( newValues ) !== JSON.stringify( oldValues ) )
                    search();
            }, {
                deep: true
            });

        return {
            ...toRefs( settings ),
            ...toRefs( requestData ),
            showReset,
            search,
            resetSearch
        };
    }
};
</script>

<style lang="scss">
    // =============================================================================
    // Variables
    // =============================================================================
    $inputWidth: calc( 100% - 85px );
    $calendarWidth: calc( 100% - 40px );

    // =============================================================================
    // Style
    // =============================================================================
    .search {
        margin-left: 34px;
        &__commands{
            gap: 15px;
        }
        &__bar {
            width: 600px;
            &__input {
                @include dimensions( $inputWidth, 50px );
                @include elements-colors ( $bg-light, $color-white );
                caret-color: $color-white;
                @include rounded-corner( 34px 0 0 34px );
                padding-left: 50px;
                @include placeholder {
                    color: $color-white;
                }
            }
            &__reset {
                @include transform-position( 50%, calc( 100% - #{$inputWidth}), false, false, translate( -50%, -50% ) );
                @include typography-specs( 16px, 16px );
            }
            &__button {
                flex: 1;
                @include position-grid( center, center );
                height: 50px;
                @include rounded-corner( 0 34px 34px 0 );
                background: $color-greenish;
            }
        }
        &__calendar {
            &__container {
                max-width: 240px;
            }
            &__input.field__input {
                @include elements-colors ( $bg-light, $color-white );
                @include dimensions( 100%, 50px );
                @include rounded-corner( 34px );
                caret-color: $color-white;
                padding-left: 60px;
            }
            &__icon{
                .a {
                    fill: $color-greenish;
                }
                .b {
                   fill: $color-white;
                }
            }
        }
        &__states, &__campaigns {
            max-width: 240px;
            &__input.field__input {
                @include elements-colors ( $bg-light, $color-white );
                @include dimensions( 100%, 50px );
                @include rounded-corner( 34px );
                @include placeholder {
                    color: $color-white;
                    text-transform: capitalize;
                }
                caret-color: $color-white;
                padding-right: 50px;
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
            }
            .field__drop{
                @include elements-colors( $color-white, $color-dark );
                @include typography-specs( 16px, 21px, 400 );
            }
            .field__autocomplete {
                &::after {
                    @include dimensions( 45px, 50px );
                    @include elements-colors ( $color-greenish, $color-white );
                    @include rounded-corner( 0 34px 34px 0 );
                }
                &--active{
                    .search__states__input, .search__campaigns__input {
                        color: $color-dark;
                        caret-color: $color-green;
                    }
                    &::after {
                        background: unset;
                    }
                }
                &__arrow {
                    path {
                        stroke: $color-white;
                    }
                    &--active {
                        path {
                            stroke: $color-green;
                        }
                    }
                }
            }
        }
    }

</style>